import { resolveLobbyApiBase } from '../../config/serviceUrl';

const BROWSER_SESSION_KEY = 'npc:auth:browser_session_v1';
const AUTH_WINDOW_ID_KEY = 'npc:auth:window_id_v1';
const LIVE_WINDOWS_KEY = 'npc:auth:live_windows_v1';
const IDLE_CLOSE_NONCE_KEY = 'npc:auth:idle_close_nonce_v1';

const LIVE_WINDOW_TTL_MS = 12_000;
const LIVE_WINDOW_HEARTBEAT_MS = 4_000;
const IDLE_CLOSE_ENDPOINT = `${resolveLobbyApiBase()}/user/logout/window-close`;

function readLocalStorage(key) {
  try { return localStorage.getItem(key); } catch (_) { return null; }
}

function writeLocalStorage(key, value) {
  try { localStorage.setItem(key, value); return true; } catch (_) { return false; }
}

function removeLocalStorage(key) {
  try { localStorage.removeItem(key); } catch (_) {}
}

function readSessionStorage(key) {
  try { return sessionStorage.getItem(key); } catch (_) { return null; }
}

function writeSessionStorage(key, value) {
  try { sessionStorage.setItem(key, value); return true; } catch (_) { return false; }
}

function removeSessionStorage(key) {
  try { sessionStorage.removeItem(key); } catch (_) {}
}

function readStoredUserCode() {
  try {
    const user = JSON.parse(readLocalStorage('user') || '{}') || {};
    return String(user?.user_code ?? user?.User_code ?? user?.userCode ?? '').trim();
  } catch (_) {
    return '';
  }
}

function makeWindowId() {
  try {
    if (globalThis?.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch (_) {}
  return `w_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function getIdleCloseNonce({ create = false } = {}) {
  let nonce = String(readSessionStorage(IDLE_CLOSE_NONCE_KEY) || '').trim();
  if (!nonce && create) {
    nonce = makeWindowId();
    writeSessionStorage(IDLE_CLOSE_NONCE_KEY, nonce);
  }
  return nonce;
}

function postIdleWindowLifecycle(action) {
  const token = String(
    readLocalStorage('token') ||
    readLocalStorage('accessToken') ||
    readLocalStorage('access_token') ||
    readLocalStorage('jwt') ||
    readLocalStorage('authToken') ||
    ''
  ).trim();
  const nonce = getIdleCloseNonce({ create: true });
  if (!token || !nonce) return false;

  const params = new URLSearchParams();
  params.set('action', String(action || ''));
  params.set('token', token);
  params.set('nonce', nonce);

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const accepted = navigator.sendBeacon(IDLE_CLOSE_ENDPOINT, params);
      if (accepted) return true;
    }
  } catch (_) {}

  try {
    void fetch(IDLE_CLOSE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
      body: params.toString(),
      keepalive: true,
      credentials: 'omit',
    }).catch(() => null);
    return true;
  } catch (_) {
    return false;
  }
}

function getWindowId({ create = false } = {}) {
  let id = String(readSessionStorage(AUTH_WINDOW_ID_KEY) || '').trim();
  if (!id && create) {
    id = makeWindowId();
    writeSessionStorage(AUTH_WINDOW_ID_KEY, id);
  }
  return id;
}

function readLiveWindows() {
  try {
    const parsed = JSON.parse(readLocalStorage(LIVE_WINDOWS_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (_) {
    return {};
  }
}

function writeLiveWindows(map) {
  const entries = Object.entries(map || {});
  if (!entries.length) {
    removeLocalStorage(LIVE_WINDOWS_KEY);
    return;
  }
  writeLocalStorage(LIVE_WINDOWS_KEY, JSON.stringify(Object.fromEntries(entries)));
}

function pruneLiveWindows(map, now = Date.now()) {
  const next = {};
  for (const [id, raw] of Object.entries(map || {})) {
    const ts = Number(raw?.ts ?? raw ?? 0);
    if (!id || !ts || now - ts > LIVE_WINDOW_TTL_MS) continue;
    next[id] = typeof raw === 'object' && raw ? raw : { ts };
  }
  return next;
}

function unregisterCurrentWindowLease() {
  const id = getWindowId({ create: false });
  if (!id) return;
  const now = Date.now();
  const map = pruneLiveWindows(readLiveWindows(), now);
  if (Object.prototype.hasOwnProperty.call(map, id)) delete map[id];
  writeLiveWindows(map);
}

export function hasStoredAuthToken() {
  return Boolean(
    readLocalStorage('token') ||
    readLocalStorage('accessToken') ||
    readLocalStorage('access_token') ||
    readLocalStorage('jwt') ||
    readLocalStorage('authToken')
  );
}

export function isAutoLoginEnabled() {
  return readLocalStorage('auto_login') === '1';
}

export function hasBrowserAuthSession() {
  return Boolean(readSessionStorage(BROWSER_SESSION_KEY));
}

export function hasFreshLiveAuthWindow() {
  const now = Date.now();
  const currentId = getWindowId({ create: false });
  const original = readLiveWindows();
  const map = pruneLiveWindows(original, now);
  if (Object.keys(map).length !== Object.keys(original).length) writeLiveWindows(map);

  return Object.entries(map).some(([id, raw]) => {
    if (currentId && id === currentId) return false;
    const ts = Number(raw?.ts ?? raw ?? 0);
    return ts > 0 && now - ts <= LIVE_WINDOW_TTL_MS;
  });
}

export function markBrowserAuthSession({ autoLogin } = {}) {
  const payload = {
    user_code: readStoredUserCode(),
    started_at: Date.now(),
  };
  writeSessionStorage(BROWSER_SESSION_KEY, JSON.stringify(payload));

  if (autoLogin === true) writeLocalStorage('auto_login', '1');
  else if (autoLogin === false) removeLocalStorage('auto_login');
}

export function clearBrowserAuthSessionState() {
  unregisterCurrentWindowLease();
  removeSessionStorage(BROWSER_SESSION_KEY);
  removeSessionStorage(AUTH_WINDOW_ID_KEY);
  removeSessionStorage(IDLE_CLOSE_NONCE_KEY);
}

export function installAuthWindowLease() {
  if (typeof window === 'undefined' || !hasStoredAuthToken()) return () => {};

  const id = getWindowId({ create: true });
  getIdleCloseNonce({ create: true });
  let stopped = false;

  // pagehide는 새로고침/화면이동에도 발생한다. 서버에 남아 있을 수 있는 종료 예약을
  // 현재 페이지가 정상적으로 다시 열린 즉시 취소한다. 같은 sessionStorage nonce를 사용하므로
  // 실제 창 종료 뒤 새 브라우저 세션이 열린 경우에는 이전 종료 예약을 잘못 취소하지 않는다.
  postIdleWindowLifecycle('cancel');

  const heartbeat = () => {
    if (stopped || !hasStoredAuthToken()) return;
    const now = Date.now();
    const map = pruneLiveWindows(readLiveWindows(), now);
    map[id] = { ts: now, user_code: readStoredUserCode() };
    writeLiveWindows(map);
  };

  const unregister = () => {
    if (stopped) return;
    const now = Date.now();
    const map = pruneLiveWindows(readLiveWindows(), now);
    if (Object.prototype.hasOwnProperty.call(map, id)) delete map[id];
    writeLiveWindows(map);
  };

  heartbeat();
  const timer = window.setInterval(heartbeat, LIVE_WINDOW_HEARTBEAT_MS);
  const onPageHide = () => {
    // 자동로그인 ON이면 창 종료 로그아웃 예약을 만들지 않는다.
    // 다른 인증 창이 살아 있으면 그 창이 세션을 사용 중이므로 역시 예약하지 않는다.
    if (!isAutoLoginEnabled() && !hasFreshLiveAuthWindow()) {
      postIdleWindowLifecycle('arm');
    }
    unregister();
  };
  window.addEventListener('pagehide', onPageHide);

  return () => {
    stopped = true;
    window.clearInterval(timer);
    window.removeEventListener('pagehide', onPageHide);
    const now = Date.now();
    const map = pruneLiveWindows(readLiveWindows(), now);
    if (Object.prototype.hasOwnProperty.call(map, id)) delete map[id];
    writeLiveWindows(map);
  };
}

export function hasLocalTableRecoveryHint() {
  try {
    for (const key of ['currentTableCode', 'lastTableCode', 'tableCode']) {
      if (String(readLocalStorage(key) || '').trim()) return true;
    }

    const desktop = JSON.parse(readLocalStorage('desktop_multitable_v1') || 'null');
    const screens = Array.isArray(desktop?.screens) ? desktop.screens : [];
    if (screens.some((screen) => {
      const tableId = String(
        screen?.tableId ||
        (String(screen?.id || '').startsWith('table:') ? String(screen.id).slice('table:'.length) : '')
      ).trim();
      return Boolean(tableId);
    })) return true;
  } catch (_) {}

  try {
    const path = String(window.location?.pathname || '');
    const sp = new URLSearchParams(String(window.location?.search || ''));
    if (path.startsWith('/table/') || path.startsWith('/popup')) return true;
    if (String(sp.get('tableCode') || sp.get('table_code') || '').trim()) return true;
  } catch (_) {}

  return false;
}

export function isColdNonAutoLoginSession() {
  if (!hasStoredAuthToken()) return false;
  if (isAutoLoginEnabled()) return false;
  if (hasBrowserAuthSession()) return false;
  if (hasFreshLiveAuthWindow()) return false;
  return true;
}

export function shouldResumeStoredSessionFromLogin() {
  if (!hasStoredAuthToken()) return false;
  return (
    isAutoLoginEnabled() ||
    hasBrowserAuthSession() ||
    hasFreshLiveAuthWindow() ||
    hasLocalTableRecoveryHint()
  );
}
